import { FunctionComponent, useMemo, useRef, useState } from "react";

import { TableColumn } from "@/components/Table/Table";

import {
	ReachOutButton,
	ReviewToSendButton,
} from "@/pages/Private/pages/List/components/ReachOut/ReachOutButton";
import { ReachOutMenu } from "@/pages/Private/pages/List/components/ReachOut/ReachOutMenu";
import {
	getAiSnippetStatus,
	getCampaignAiSnippetColumnIds,
} from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/utils/getAiSnippetStatus";

import { ListPerson } from "../../schema/list";
import { resolveLeadReviewTarget } from "../resolveLeadReviewTarget";
import { isDummyProspect } from "./dummyProspect";
import { getReachOutStatus } from "./reachOutStatus";

interface OutreachCellProps {
	row: ListPerson;
	/** Open the lead's chat (drafts / manual composer / reply thread) — threaded from the table. */
	onOpenChat?: (row: ListPerson) => void;
}

/**
 * The Outreach column cell: the state-driven {@link ReachOutButton} and, on demand, the portaled
 * {@link ReachOutMenu}. Placeholder ("Find leads") rows carry no contact, so they render nothing.
 * The contact-channel status icons + CRM badges live in the Name column.
 */
const OutreachCell: FunctionComponent<OutreachCellProps> = ({ row, onOpenChat }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
	const anchorRef = useRef<HTMLDivElement>(null);

	// Resolve the AI-snippet lifecycle for the lead's assigned campaign (null when it uses none).
	const aiSnippetStatus = useMemo(() => {
		const assignedCampaign =
			row.campaignConfig ??
			(row.campaignId ? row.list?.campaignConfig?.[row.campaignId] : undefined);

		if (!assignedCampaign) {
			return null;
		}

		return getAiSnippetStatus(
			row,
			getCampaignAiSnippetColumnIds(assignedCampaign.initialNodes, row.list?.columns)
		);
	}, [row]);

	const status = useMemo(
		() =>
			getReachOutStatus({
				assignStatus: row.assignStatus,
				copywritingStatus: row.copywritingStatus,
				hasCopywritingOutput: !!row.copywritingOutput,
				aiSnippetStatus,
			}),
		[row.assignStatus, row.copywritingStatus, row.copywritingOutput, aiSnippetStatus]
	);

	if (isDummyProspect(row) || !row.prospect) {
		return null;
	}

	const openMenu = () => {
		setAnchorRect(anchorRef.current?.getBoundingClientRect() ?? null);
		setMenuOpen(true);
	};

	const openChat = () => onOpenChat?.(row);

	// A lead with a `campaignId` is already routed to a campaign, so its primary Outreach action is to
	// review that outreach rather than to (re)launch reach-out. Where that review happens depends on
	// the lead: the Outbox once the campaign owns it, the Inbox once it has replied, Review-to-send
	// while it is still awaiting approval — `resolveLeadReviewTarget` is the single place that decides
	// (PREC-4103). This takes over the `default`/`ready` states; the more urgent `reply` and
	// in-progress `generating` states are kept.
	const reviewHref =
		row.campaignId && (status.state === "default" || status.state === "ready")
			? resolveLeadReviewTarget(row)
			: undefined;

	return (
		<div className="flex items-center">
			<div ref={anchorRef}>
				{reviewHref ? (
					<ReviewToSendButton href={reviewHref} />
				) : (
					<ReachOutButton
						forceVisible={menuOpen}
						status={status}
						onReachOut={openMenu}
						onReply={openChat}
						onReview={openChat}
					/>
				)}
			</div>

			{menuOpen && (
				<ReachOutMenu
					anchorRect={anchorRect}
					target={{ kind: "lead", row }}
					onClose={() => setMenuOpen(false)}
					onManual={openChat}
				/>
			)}
		</div>
	);
};

/**
 * Outreach column — sits right after Name in the leads table. Owns the per-lead reach-out affordance
 * (button + menu); the contact-channel status icons + CRM badges render in the Name column. Inserted
 * after the Name column for every list type by `insertOutreachColumn`.
 */
export const renderOutreachColumn = (
	ts: (key: string) => string,
	onOpenChat?: (row: ListPerson) => void
): TableColumn => ({
	title: ts("outreach"),
	uid: "outreach",
	minWidth: "160px",
	calcWidth: 160,
	width: "fit-content",
	moveable: true,
	hideable: true,
	show: true,
	render: (row: ListPerson) => <OutreachCell row={row} onOpenChat={onOpenChat} />,
});
